import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Calendar, MapPin, Smartphone, Loader2 } from 'lucide-react';
import api, { getImageUrl } from '../api';

function ImagePreviewModal({ isOpen, onClose, imageUrl, lat, lng, address }) {
  if (!isOpen) return null;
  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={onClose}></div>
      <div className="relative max-w-sm w-full bg-slate-900/50 rounded-2xl overflow-hidden shadow-2xl border border-white/10 animate-in zoom-in duration-200">
        <button onClick={onClose} className="absolute top-3 right-3 z-20 bg-black/40 hover:bg-black/60 text-white p-1.5 rounded-full transition-colors">
          <X size={16} strokeWidth={3} />
        </button>
        <div className="aspect-square relative">
          <img src={imageUrl} alt="preview" className="w-full h-full object-cover bg-black" />
        </div>
        
        {/* Location Info */}
        <div className="p-4 bg-gradient-to-t from-slate-950 to-slate-900 border-t border-white/10 shadow-inner text-left">
          <div className="flex items-center gap-2 text-emerald-400 mb-2">
            <MapPin size={16} fill="currentColor" fillOpacity={0.2} />
            <h4 className="font-bold text-xs">Live Location Tracking</h4>
          </div>
          <div className="flex flex-col gap-2">
             <div className="bg-white/5 rounded-lg p-2.5 ring-1 ring-white/5 min-h-[40px] flex items-center">
                <p className="text-[11px] font-bold text-slate-100/90 leading-tight">
                  {address ? address : (lat && lng ? `Lat: ${lat}, Long: ${lng}` : 'Office HQ (Static)')}
                </p>
             </div>
             {lat && lng && (
                 <a 
                   href={`https://www.google.com/maps?q=${lat},${lng}`} 
                   target="_blank" 
                   rel="noreferrer"
                   className="w-full bg-[#10b981] hover:bg-emerald-500 text-white py-2 rounded-lg text-center text-xs font-black transition-all shadow-lg shadow-emerald-500/10 active:scale-95 flex items-center justify-center gap-2"
                 >
                   <MapPin size={14} fill="currentColor" fillOpacity={0.2} />
                   View on Live Map
                 </a>
             )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function AttendanceHistoryModal({ isOpen, onClose, employee }) {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const itemsPerPage = 15;
  const [previewImage, setPreviewImage] = useState(null);
  const [brokenImages, setBrokenImages] = useState(new Set());

  const handleImageError = (imageUrl) => {
    setBrokenImages(prev => {
      const next = new Set(prev);
      next.add(imageUrl);
      return next;
    });
  };

  useEffect(() => {
    if (isOpen && employee?.user_id) {
      setBrokenImages(new Set());
      setCurrentPage(1);
      setDateRange({ start: '', end: '' });
      fetchHistory();
    }
  }, [isOpen, employee]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/attendance/user/${employee.user_id}`);
      const data = Array.isArray(response.data) ? response.data : (response.data.data || []);
      
      const formatTime = (ts) => {
        if (!ts) return '--:--';
        return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
      };

      const transformed = data.map(record => ({
        ...record,
        date: record.date,
        status: record.status?.toUpperCase() || 'ABSENT',
        inTime: formatTime(record.clock_in),
        outTime: formatTime(record.clock_out),
        // Trim path to fix potential broken URLs from messy strings
        inPhoto: getImageUrl((record.clock_in_image || '').trim()),
        outPhoto: getImageUrl((record.clock_out_image || '').trim()),
        locationLabel: record.clock_in_latitude ? 'Office' : 'Office', // User said change Remote to Office
        hours: record.work_hours || '--',
        remarks: record.remarks || ''
      }));
      
      setHistoryData(transformed);
    } catch (err) {
      console.error("Error fetching history:", err);
      setError('Failed to load history.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !employee) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'PRESENT': return 'bg-emerald-100 text-emerald-600 border border-emerald-200';
      case 'ABSENT': return 'bg-red-50 text-red-500 border border-red-100';
      case 'HALF DAY': return 'bg-blue-50 text-blue-500 border border-blue-100';
      default: return 'bg-gray-100 text-gray-500';
    }
  };

  const filteredData = historyData.filter(item => {
    if (dateRange.start && dateRange.end) {
      return item.date >= dateRange.start && item.date <= dateRange.end;
    } else if (dateRange.start) {
      return item.date === dateRange.start;
    }
    return true;
  });

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const currentData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return createPortal(
    <div className="fixed inset-0 z-[999] flex justify-end">
      {/* Blurred Overlay */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity" onClick={onClose}></div>
      
      {/* Slide-over Drawer */}
      <div className="relative bg-gray-50 shadow-2xl w-[90%] md:w-[800px] lg:w-[950px] h-full flex flex-col overflow-hidden animate-in slide-in-from-right duration-300">
        
        {/* Header - Fixed */}
        <div className="bg-white px-6 py-4 flex flex-col gap-4 border-b border-gray-200 z-10 shrink-0">
          
          {/* Top Row: User Info & Close */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {employee.avatar ? (
                <img src={employee.avatar} alt={employee.name} className="w-12 h-12 rounded-full object-cover border-2 border-emerald-100 shadow-sm" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xl font-bold uppercase shadow-sm">
                  {employee.name.charAt(0)}
                </div>
              )}
              <div>
                <h2 className="text-xl font-black text-slate-800 capitalize leading-tight">{employee.name}</h2>
                <p className="text-xs font-semibold text-gray-500 mt-1 uppercase tracking-wide">
                  <span className="text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">{employee.designation}</span> • {employee.name.replace(/\s+/g, '').toLowerCase()}@gmail.com
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors">
              <X size={24} />
            </button>
          </div>

          {/* Bottom Row: Title & Dates */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pt-2 mt-2 border-t border-gray-100 gap-4">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-blue-50 text-blue-600">
                <Calendar size={18} strokeWidth={2.5} />
              </div>
              <h3 className="text-lg font-bold text-slate-700">Attendance History</h3>
            </div>

            <div className="flex items-center border border-gray-200 rounded-md bg-white px-3 py-1.5 shadow-sm focus-within:ring-2 focus-within:ring-[#10b981]/20">
              <input 
                 type="date"
                 value={dateRange.start}
                 onChange={(e) => { setDateRange(prev => ({ ...prev, start: e.target.value })); setCurrentPage(1); }}
                 className="text-xs font-semibold text-slate-500 focus:outline-none bg-transparent"
                 title="Start Date"
              />
              <span className="text-slate-300 mx-2">-</span>
              <input 
                 type="date"
                 value={dateRange.end}
                 onChange={(e) => { setDateRange(prev => ({ ...prev, end: e.target.value })); setCurrentPage(1); }}
                 className="text-xs font-semibold text-slate-500 focus:outline-none bg-transparent"
                 title="End Date"
              />
              {(dateRange.start || dateRange.end) && (
                <X size={14} className="ml-2 text-slate-400 cursor-pointer hover:text-red-500 shrink-0" onClick={() => { setDateRange({ start: '', end: '' }); setCurrentPage(1); }}/>
              )}
            </div>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar bg-slate-50/50">
          {loading ? (
            <div className="h-full flex flex-col items-center justify-center text-slate-400 gap-3">
              <Loader2 className="animate-spin" size={40} />
              <p className="font-bold">Fetching history records...</p>
            </div>
          ) : error ? (
            <div className="h-full flex flex-col items-center justify-center text-red-500 gap-3">
              <p className="font-bold">{error}</p>
              <button onClick={fetchHistory} className="text-xs bg-red-100 px-3 py-1.5 rounded-md hover:bg-red-200 transition-colors">Retry</button>
            </div>
          ) : historyData.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-slate-400 gap-3">
              <Calendar size={40} opacity={0.5} />
              <p className="font-bold">No attendance records found for this period.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {currentData.map((item, idx) => (
                  <div key={idx} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow flex flex-col relative overflow-hidden group">
                    {/* Date & Status */}
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[12px] font-black text-slate-700 tracking-wide">{item.date}</span>
                      <span className={`text-[8px] font-black px-1.5 py-0.5 rounded tracking-widest ${getStatusColor(item.status)}`}>
                        {item.status}
                      </span>
                    </div>

                    {/* Clock In */}
                    <div className={`p-2 rounded-lg border flex items-center justify-between mb-2 ${item.inTime !== '--:--' ? 'bg-emerald-50/30 border-emerald-100 shadow-sm' : 'bg-gray-50/50 border-gray-100'}`}>
                      <div>
                        <span className="text-[9px] font-bold text-emerald-600 block mb-0.5 tracking-wider uppercase">Clock In</span>
                        <span className={`text-[16px] font-black tracking-tight ${item.inTime !== '--:--' ? 'text-slate-800' : 'text-slate-400'}`}>{item.inTime}</span>
                      </div>
                      {item.inPhoto && !item.inPhoto.endsWith('/storage/') && !brokenImages.has(item.inPhoto) ? (
                        <img 
                          src={item.inPhoto} 
                          alt="in" 
                          onError={() => handleImageError(item.inPhoto)}
                          onClick={() => setPreviewImage({ 
                            url: item.inPhoto, 
                            lat: item.clock_in_latitude, 
                            lng: item.clock_in_longitude,
                            address: item.clock_in_address
                          })}
                          className="w-[36px] h-[38px] rounded object-cover border border-emerald-100 shadow-sm cursor-zoom-in hover:brightness-90 transition-all" 
                        />
                      ) : (
                        <div className="w-[36px] h-[38px] rounded bg-white border border-gray-200 flex items-center justify-center text-emerald-300">
                          <Smartphone size={14} strokeWidth={2} />
                        </div>
                      )}
                    </div>

                    {/* Clock Out */}
                    <div className={`p-2 rounded-lg border flex items-center justify-between mb-3 ${item.outTime !== '--:--' ? 'bg-blue-50/30 border-blue-100 shadow-sm' : 'bg-gray-50/50 border-gray-100'}`}>
                      <div>
                        <span className="text-[9px] font-bold text-slate-500 block mb-0.5 tracking-wider uppercase">Clock Out</span>
                        <span className={`text-[16px] font-black tracking-tight ${item.outTime !== '--:--' ? 'text-slate-800' : 'text-slate-400'}`}>{item.outTime}</span>
                      </div>
                      {item.outPhoto && !item.outPhoto.endsWith('/storage/') && !brokenImages.has(item.outPhoto) ? (
                        <img 
                          src={item.outPhoto} 
                          alt="out" 
                          onError={() => handleImageError(item.outPhoto)}
                          onClick={() => setPreviewImage({ 
                            url: item.outPhoto, 
                            lat: item.clock_out_latitude, 
                            lng: item.clock_out_longitude,
                            address: item.clock_out_address
                          })}
                          className="w-[36px] h-[38px] rounded object-cover border border-gray-200 shadow-sm cursor-zoom-in hover:brightness-90 transition-all" 
                        />
                      ) : (
                        <div className="w-[36px] h-[38px] rounded bg-white border border-gray-200 flex items-center justify-center text-slate-300">
                          <Smartphone size={14} strokeWidth={2} />
                        </div>
                      )}
                    </div>

                    {/* Remarks/Alerts */}
                    {item.remarks && (
                      <div className="mb-3">
                        <p className="text-[9px] font-bold text-red-500 border border-red-100 bg-red-50/50 py-1 px-2.5 rounded-md text-center shadow-sm">
                          • {item.remarks}
                        </p>
                      </div>
                    )}

                    {/* Bottom Stats */}
                    <div className="mt-auto pt-3 border-t border-gray-100 flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-gray-400">
                        <MapPin size={10} />
                        <span className="text-[10px] font-bold">{item.locationLabel}</span>
                      </div>
                      <span className={`text-[10px] font-bold ${item.hours !== '--' ? 'text-blue-500 bg-blue-50' : 'text-gray-400 bg-gray-100'} px-1.5 py-0.5 rounded`}>
                        {item.hours} hrs
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              
              {totalPages > 1 ? (
                <div className="mt-8 flex justify-center items-center gap-2 pb-6">
                  <button 
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 text-xs font-bold text-gray-500 hover:bg-gray-200 bg-gray-100 rounded-md disabled:opacity-50 transition-colors shadow-sm"
                  >
                    Previous
                  </button>
                  <span className="text-xs font-semibold text-slate-500 mx-2">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button 
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-gray-200 bg-gray-100 rounded-md disabled:opacity-50 transition-colors shadow-sm"
                  >
                    Next
                  </button>
                </div>
              ) : (
                <div className="mt-8 flex justify-center items-center gap-2 pb-6">
                  <p className="text-xs font-bold text-slate-400">Showing all historical records for this span.</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Image Preview Modal */}
      {previewImage && (
        <ImagePreviewModal 
          isOpen={!!previewImage} 
          onClose={() => setPreviewImage(null)} 
          imageUrl={previewImage.url}
          lat={previewImage.lat}
          lng={previewImage.lng}
          address={previewImage.address}
        />
      )}
    </div>,
    document.body
  );
}
